<template>
  <div v-if="loading" class="flex items-center justify-center py-6">
    <ProgressSpinner style="width: 30px; height: 30px" />
    <span class="ml-2 text-sm text-gray-500">
      {{ $t('StickyQuestionResults.loading') || 'Loading...' }}
    </span>
  </div>

  <div v-else-if="stickyResults.length > 0" class="w-full">
    <!-- Header -->
    <div class="mb-1">
      <h3 class="text-gray-900 text-base lg:text-lg font-bold">
        {{ $t('StickyQuestionResults.timelineTitle') || 'Your Results Over Time' }}
      </h3>
      <div class="flex items-center justify-between mt-0.5">
        <p class="text-xs text-gray-400">
          {{ $t('StickyQuestionResults.timelineSubtitle')
            || 'Click a week to see details' }}
        </p>
        <label
          v-if="!isMobile"
          class="flex items-center gap-2 text-xs text-gray-500 cursor-pointer
           select-none hover:text-gray-700"
        >
          <ToggleSwitch v-model="showOnlyWithAnswers" />
          {{ $t('StickyQuestionResults.onlyWithAnswers')
            || 'Only weeks with answers' }}
        </label>
      </div>
    </div>

    <!-- Desktop Timeline (expandable accordion) -->
    <div v-if="!isMobile" class="space-y-2 mt-3">
      <div
        v-for="entry in pagedDesktopResults"
        :key="entry.assessmentId"
        class="border rounded-xl overflow-hidden transition-all duration-200"
        :class="isExpanded(entry)
          ? 'bg-white border-gray-300 shadow-sm'
          : isSelected(entry) || isHighlightedByChart(entry)
            ? 'bg-blue-50/50 border-blue-200 shadow-sm ring-1 ring-blue-100'
            : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'"
      >
        <!-- Row header (always visible) -->
        <div
          class="flex items-center gap-4 px-4 py-3.5 cursor-pointer"
          :class="isExpanded(entry) ? 'border-b border-gray-100' : ''"
          @click="toggleEntry(entry)"
        >
          <!-- Left: Gesamtmittelwert -->
          <div class="flex-shrink-0 w-16 text-center">
            <span
              class="text-2xl font-bold leading-none"
              :style="{ color: getScoreColor(getGesamtmittelwert(entry)) }"
            >
              {{ formatKpi(getGesamtmittelwert(entry)) }}
            </span>
            <span class="block text-sm text-gray-400 font-medium mt-0.5">/ 5</span>
          </div>

          <!-- Middle: Date + answer quote -->
          <div class="flex-1 min-w-0">
            <p class="text-lg font-medium text-gray-500">
              {{ formatDate(entry.completedAt) }}
            </p>
            <p
              v-if="getFirstOpenAnswer(entry)"
              class="text-sm text-gray-700 mt-1 truncate"
            >
              {{ getQuotedAnswer(entry) }}
            </p>
          </div>

          <!-- Right: Chevron -->
          <i
            class="pi text-gray-400 text-xs flex-shrink-0 transition-transform duration-200"
            :class="isExpanded(entry) ? 'pi-chevron-down' : 'pi-chevron-right'"
          />
        </div>

        <!-- Expanded detail (accordion content) -->
        <div v-if="isExpanded(entry)" class="px-4 pb-4">
          <WeekDetailPanel
            :selected-data="{
              dateLabel: getEntryDateKey(entry),
              completedAt: entry.completedAt,
              kpiValues: filterKpiForChart(entry.kpiValues || {}),
              openAnswers: entry.openAnswers || [],
              stickyAnswers: entry.stickyAnswers || [],
            }"
            :dimensions="dimensions"
            :dimension-colors="dimensionColors"
          />
        </div>
      </div>
    </div>

    <!-- Paginator: same pattern as CMS DataTable (UsersManager) — not v-else-chained to mobile -->
    <Paginator
      v-if="!isMobile && resultsNewestFirst.length > 0"
      :first="first"
      :rows="rows"
      :total-records="resultsNewestFirst.length"
      :rows-per-page-options="[10, 20, 50, 100]"
      template="FirstPageLink PrevPageLink PageLinks NextPageLink
       LastPageLink CurrentPageReport RowsPerPageDropdown"
      :current-page-report-template="paginatorReportTemplate"
      class="mt-4"
      @page="onDesktopPage"
    />

    <!-- Mobile Simplified Timeline (last 5 weeks) -->
    <div v-if="isMobile" class="space-y-2 mt-3">
      <div
        v-for="entry in mobileResults"
        :key="entry.assessmentId"
        class="border border-gray-200/60 rounded-xl p-3 flex items-center gap-3"
      >
        <div class="flex-shrink-0 w-11 text-center">
          <span
            class="text-lg font-bold leading-none"
            :style="{ color: getScoreColor(getGesamtmittelwert(entry)) }"
          >
            {{ formatKpi(getGesamtmittelwert(entry)) }}
          </span>
          <span class="block text-[9px] text-gray-400">/ 5</span>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-[11px] font-medium text-gray-500">
            {{ formatDate(entry.completedAt) }}
          </p>
          <p
            v-if="getFirstOpenAnswer(entry)"
            class="text-xs text-gray-600 mt-0.5 truncate"
          >
            {{ getQuotedAnswer(entry) }}
          </p>
        </div>
        <i class="pi pi-chevron-right text-gray-300 text-[10px] flex-shrink-0" />
      </div>

      <!-- Desktop hint -->
      <div class="mt-3 py-3 px-4 bg-blue-50/50 border border-blue-100
       rounded-lg text-center">
        <p class="text-xs text-blue-600">
          {{ $t('StickyQuestionResults.desktopHint')
            || 'Detailed analysis available on desktop.' }}
        </p>
      </div>
    </div>

    <!-- Empty filtered state -->
    <div
      v-if="!isMobile && filteredResults.length === 0 && stickyResults.length > 0"
      class="text-sm text-gray-400 italic py-4 text-center"
    >
      {{ $t('StickyQuestionResults.noFilteredResults')
        || 'No weeks with answers found in this period.' }}
    </div>
  </div>

  <!-- Full empty state -->
  <div
    v-else-if="!loading && stickyResults.length === 0"
    class="w-full"
  >
    <p class="text-sm text-gray-400 italic py-4 text-center">
      {{ $t('StickyQuestionResults.noResults')
        || 'No data available for the selected period.' }}
    </p>
  </div>
</template>

<script>
import ProgressSpinner from 'primevue/progressspinner';
import ToggleSwitch from 'primevue/toggleswitch';
import Paginator from 'primevue/paginator';
import { adminApi } from '@/includes/contentManagementApi';
import WeekDetailPanel from './WeekDetailPanel.vue';
import { formatLocaleDate } from '@/helpers/formatLocaleDate';
import { formatOwnResultNumber } from '@/helpers/formatOwnResultNumber';

const DEFAULT_ROWS = 10;

export default {
  name: 'StickyQuestionResults',
  components: { ProgressSpinner, ToggleSwitch, Paginator, WeekDetailPanel },
  emits: ['select-entry', 'data-loaded'],
  props: {
    schemaId: {
      type: String,
      required: true,
    },
    selectedAssessmentId: {
      type: String,
      default: null,
    },
    hoveredDate: {
      type: String,
      default: null,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    dimensions: {
      type: Array,
      default: () => [],
    },
    dimensionColors: {
      type: Object,
      default: () => ({}),
    },
    /** @deprecated Optional filter; default (empty) shows all dimensions from the API. */
    chartDimensionKeys: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      stickyResults: [],
      showOnlyWithAnswers: false,
      expandedIds: new Set(),
      first: 0,
      rows: DEFAULT_ROWS,
    };
  },
  computed: {
    filteredResults() {
      if (!this.showOnlyWithAnswers) return this.stickyResults;
      return this.stickyResults.filter(
        (e) => {
          const hasOpenAnswers = e.openAnswers?.some(
            (a) => a.answer != null && a.answer !== '',
          );
          const hasStickyAnswers = e.stickyAnswers?.some(
            (a) => a.answer != null && a.answer !== '',
          );
          return hasOpenAnswers || hasStickyAnswers;
        },
      );
    },
    mobileResults() {
      return this.stickyResults.slice(0, 5);
    },
    /** Newest first; entries without completedAt sort last */
    resultsNewestFirst() {
      const list = [...this.filteredResults];
      list.sort((a, b) => {
        const ta = a.completedAt ? new Date(a.completedAt).getTime() : null;
        const tb = b.completedAt ? new Date(b.completedAt).getTime() : null;
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        return tb - ta;
      });
      return list;
    },
    pagedDesktopResults() {
      return this.resultsNewestFirst.slice(this.first, this.first + this.rows);
    },
    paginatorReportTemplate() {
      return this.$t('StickyQuestionResults.paginatorCurrentPageReport');
    },
  },
  watch: {
    schemaId: {
      handler(newVal) {
        this.first = 0;
        this.rows = DEFAULT_ROWS;
        if (newVal) this.fetchStickyResults();
      },
      immediate: true,
    },
    showOnlyWithAnswers() {
      this.first = 0;
    },
    resultsNewestFirst: {
      handler(list) {
        if (this.first >= list.length) this.first = 0;
      },
      deep: false,
    },
  },
  methods: {
    onDesktopPage(event) {
      this.first = event.first;
      if (event.rows != null && event.rows !== this.rows) {
        this.rows = event.rows;
      }
    },
    /**
     * Pass through all KPI dimensions unless an explicit key filter is provided.
     */
    filterKpiForChart(kpiValues) {
      if (!kpiValues || typeof kpiValues !== 'object') return {};
      const keys = this.chartDimensionKeys;
      if (!keys || keys.length === 0) return { ...kpiValues };
      const out = {};
      keys.forEach((k) => {
        if (kpiValues[k] !== undefined && kpiValues[k] !== null) {
          out[k] = kpiValues[k];
        }
      });
      return Object.keys(out).length > 0 ? out : { ...kpiValues };
    },

    async fetchStickyResults() {
      if (!this.schemaId) return;
      this.loading = true;
      try {
        const { data } = await adminApi.get(
          `/assessments/user/sticky-results/${this.schemaId}`,
        );
        this.stickyResults = data.results || [];
        this.$emit('data-loaded', this.stickyResults);
      } catch (err) {
        console.error('Failed to fetch sticky results:', err);
        this.stickyResults = [];
        this.$emit('data-loaded', []);
      } finally {
        this.loading = false;
      }
    },

    getEntryDateKey(entry) {
      if (!entry.completedAt) return '';
      return new Date(entry.completedAt).toISOString().split('T')[0];
    },

    isSelected(entry) {
      return this.selectedAssessmentId === entry.assessmentId;
    },

    isHighlightedByChart(entry) {
      return Boolean(
        this.hoveredDate && this.entryMatchesDateLabel(entry, this.hoveredDate),
      );
    },

    entryMatchesDateLabel(entry, label) {
      if (!entry.completedAt || !label) return false;
      const entryDate = new Date(entry.completedAt).toISOString().split('T')[0];
      if (entryDate === label) return true;
      if (label.endsWith('-01') && label.length === 10) {
        return entryDate.substring(0, 7) === label.substring(0, 7);
      }
      const labelDate = new Date(label);
      const entryDateObj = new Date(entry.completedAt);
      return this.getISOWeek(entryDateObj) === this.getISOWeek(labelDate);
    },

    getISOWeek(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
      return `${d.getUTCFullYear()}-W${weekNum}`;
    },

    isExpanded(entry) {
      return this.expandedIds.has(entry.assessmentId);
    },

    toggleEntry(entry) {
      const next = new Set(this.expandedIds);
      if (next.has(entry.assessmentId)) {
        next.delete(entry.assessmentId);
      } else {
        next.add(entry.assessmentId);
      }
      this.expandedIds = next;
      this.$emit('select-entry', {
        assessmentId: entry.assessmentId,
        dateKey: this.getEntryDateKey(entry),
      });
    },

    getGesamtmittelwert(entry) {
      const kpis = this.filterKpiForChart(entry.kpiValues || {});
      if (!kpis || Object.keys(kpis).length === 0) return null;
      const values = Object.values(kpis)
        .map(Number)
        .filter((v) => !Number.isNaN(v));
      if (values.length === 0) return null;
      const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
      return Math.round(avg * 100) / 100;
    },

    getFirstOpenAnswer(entry) {
      const answers = entry.openAnswers || entry.stickyAnswers || [];
      const textAnswer = answers.find(
        (a) => a.answer != null && a.answer !== '' && a.type !== 'rangeSlider',
      );
      return textAnswer?.answer || null;
    },

    getQuotedAnswer(entry) {
      const answer = this.getFirstOpenAnswer(entry);
      if (!answer) return '';
      const truncated = answer.length > 100 ? `${answer.substring(0, 100)}\u2026` : answer;
      return `\u201E${truncated}\u201C`;
    },

    getScoreColor(value) {
      const num = Number(value);
      if (Number.isNaN(num) || value == null) return '#475569';
      if (num >= 4) return '#2563EB';
      if (num >= 3) return '#475569';
      return '#F59E0B';
    },

    formatDate(dateStr) {
      return formatLocaleDate(dateStr, this.$i18n?.locale);
    },

    formatKpi(value) {
      return formatOwnResultNumber(value);
    },
  },
};
</script>
